import Loader from "@/components/Loader/Loader";
import { Card } from "@/components/ui/card";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

export type HourlySalesLegacyPoint = {
  hour: string;
  value: number;
  tooltipLabel?: string;
};

export const REPORT_STATUS_KEYS = [
  "paid",
  "awaiting_payment",
  "not_authorized",
  "chargeback",
  "refunded",
] as const;

export type SeriesConfig = {
  color: string;
  gradientId: string;
  yAxisId?: string;
};

export const STATUS_SERIES_CONFIG: Record<
  (typeof REPORT_STATUS_KEYS)[number],
  SeriesConfig
> = {
  paid: { color: "#A9D342", gradientId: "gradient-paid" },
  awaiting_payment: { color: "#F59E0B", gradientId: "gradient-awaiting" },
  not_authorized: { color: "#DC2626", gradientId: "gradient-not-authorized" },
  chargeback: { color: "#8B5CF6", gradientId: "gradient-chargeback" },
  refunded: { color: "#6B7280", gradientId: "gradient-refunded" },
};

export type HourlySalesMultiStatusPoint = {
  hour: string;
} & Partial<
  Record<
    (typeof REPORT_STATUS_KEYS)[number],
    number
  >
> & Partial<
  Record<`${(typeof REPORT_STATUS_KEYS)[number]}_formatted`, string>
>;

export type HourlySalesPoint = HourlySalesLegacyPoint | HourlySalesMultiStatusPoint;

function isLegacyFormat(
  data: HourlySalesPoint[]
): data is HourlySalesLegacyPoint[] {
  if (!data.length) return true;
  const first = data[0] as Record<string, unknown>;
  return "value" in first && typeof first.value === "number" && !("paid" in first);
}

function getActiveStatusKeys(data: HourlySalesMultiStatusPoint[]): (typeof REPORT_STATUS_KEYS)[number][] {
  if (!data.length) return [];
  const first = data[0] as Record<string, unknown>;
  return REPORT_STATUS_KEYS.filter(
    (key) =>
      key in first &&
      (typeof first[key] === "number" || typeof first[key] === "string")
  );
}

interface HourlySalesChartProps {
  hourlySales?: HourlySalesPoint[];
  isLoadingData: boolean;
  text?: string;
  width?: string;
  title?: string;
  seriesKeys?: readonly string[];
  seriesConfig?: Record<string, SeriesConfig>;
  seriesTranslationNs?: string;
}

export default function HourlySalesChart({
  hourlySales = [],
  isLoadingData,
  text,
  width = "md:w-3/5",
  title,
  seriesKeys = REPORT_STATUS_KEYS,
  seriesConfig = STATUS_SERIES_CONFIG,
  seriesTranslationNs = 'common',
}: HourlySalesChartProps) {
  const { t } = useTranslation(["dashboard", "common", "signatures"]);
  const isMobile = useIsMobile();
  const isLegacy = isLegacyFormat(hourlySales);
  const activeStatusKeys = isLegacy
    ? []
    : (hourlySales.length
        ? seriesKeys.filter((key) => {
            const first = hourlySales[0] as Record<string, unknown>;
            return (
              key in first &&
              (typeof first[key] === 'number' || typeof first[key] === 'string')
            );
          })
        : []);
  const activeStatusKeysOrdered = activeStatusKeys;

  const yAxisIds = useMemo(() => {
    if (isLegacy || !activeStatusKeysOrdered.length) return ['default'];
    const ids = activeStatusKeysOrdered.map(
      (key) => seriesConfig[key]?.yAxisId ?? 'default',
    );
    return ids.filter((id, index) => ids.indexOf(id) === index);
  }, [isLegacy, activeStatusKeysOrdered, seriesConfig]);

  const useDualAxis = yAxisIds.length > 1;

  return (
    <Card
      className={`bg-[#09090B] border-zinc-800 rounded-lg flex-grow ${width}`}
    >
      <div className="flex items-center gap-2 mb-6 border-b border-[#27272A] p-4">
        <h2 className="text-lg font-semibold text-white">{title || t("paidSales")}</h2>
        {text && (
          <div className="rounded bg-[#18181B] px-2 inline-block justify-center">
            <span className="text-xs text-zinc-400">{text}</span>
          </div>
        )}
      </div>

      <div className="h-[240px] md:h-[320px] mx-4 relative p-4">
        {isLoadingData ? (
          <div className="flex h-full items-center justify-center">
            <Loader />
          </div>
        ) : (
          <div className="h-full">
            {hourlySales.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={hourlySales}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  style={isMobile ? { touchAction: "pan-y" } : undefined}
                >
                  <defs>
                    <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#A9D342" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#A9D342" stopOpacity={0.1} />
                    </linearGradient>
                    {seriesKeys.map((key) => (
                      <linearGradient
                        key={key}
                        id={seriesConfig[key]?.gradientId ?? `gradient-${key}`}
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor={seriesConfig[key]?.color ?? '#A9D342'}
                          stopOpacity={0.8}
                        />
                        <stop
                          offset="95%"
                          stopColor={seriesConfig[key]?.color ?? '#A9D342'}
                          stopOpacity={0.1}
                        />
                      </linearGradient>
                    ))}
                  </defs>

                  <XAxis
                    dataKey="hour"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#888888", fontSize: 12 }}
                    dy={10}
                  />
                  <YAxis yAxisId="default" hide={true} />
                  {useDualAxis && (
                    <>
                      <YAxis
                        yAxisId="count"
                        hide={true}
                        domain={['auto', 'auto']}
                      />
                      <YAxis
                        yAxisId="revenue"
                        orientation="right"
                        hide={true}
                        domain={['auto', 'auto']}
                      />
                    </>
                  )}
                  <CartesianGrid
                    stroke="#27272A"
                    strokeDasharray="0"
                    vertical={false}
                    horizontal={true}
                  />

                  {!isMobile && (
                    <Tooltip
                      cursor={false}
                      content={({ active, payload }) => {
                        if (!active || !payload || payload.length === 0) return null;

                        const data = payload[0].payload as Record<string, unknown>;

                        if (isLegacy) {
                          return (
                            <div className="bg-[#09090B] border border-borderColor px-3 py-2 rounded-lg">
                              <p className="text-sm text-zinc-100">{String(data.hour)}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <div className="w-1 h-4 bg-[#A9D342]" />
                                <span className="text-zinc-400 text-xs">{t("paid")}</span>
                                <span className="text-white text-xs font-medium">
                                  {String((data.tooltipLabel as string) ?? data.value ?? "")}
                                </span>
                              </div>
                            </div>
                          );
                        }

                        return (
                          <div className="bg-[#09090B] border border-borderColor px-3 py-2 rounded-lg min-w-[140px]">
                            <p className="text-sm text-zinc-100 mb-2">{String(data.hour)}</p>
                            <div className="flex flex-col gap-1">
                              {activeStatusKeys.map((key) => {
                                const value = data[key] as number | undefined;
                                const formatted =
                                  (data[`${key}_formatted`] as string) ??
                                  (typeof value === "number" ? String(value) : "—");
                                if (value === undefined && formatted === "—") return null;
                                const config = seriesConfig[key] ?? { color: '#A9D342', gradientId: `gradient-${key}` };
                                const labelKey = seriesTranslationNs === 'signatures'
                                  ? `${seriesTranslationNs}:chart.${key}`
                                  : `${seriesTranslationNs}:${key}`;
                                return (
                                  <div
                                    key={key}
                                    className="flex items-center gap-2 justify-between"
                                  >
                                    <div className="flex items-center gap-2">
                                      <div
                                        className="w-1 h-4 rounded-sm shrink-0"
                                        style={{ backgroundColor: config.color }}
                                      />
                                      <span className="text-zinc-400 text-xs">
                                        {t(labelKey)}
                                      </span>
                                    </div>
                                    <span className="text-white text-xs font-medium">
                                      {formatted}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      }}
                    />
                  )}

                  {isLegacy ? (
                    <Area
                      type="linear"
                      dataKey="value"
                      yAxisId="default"
                      stroke="#A9D342"
                      fill="url(#colorGradient)"
                      strokeWidth={2}
                      dot={false}
                      isAnimationActive={!isMobile}
                      activeDot={
                        isMobile
                          ? false
                          : {
                              r: 4,
                              fill: "#A9D342",
                              stroke: "#A9D342",
                              strokeWidth: 2,
                            }
                      }
                    />
                  ) : (
                    activeStatusKeysOrdered.map((key) => {
                      const config = seriesConfig[key] ?? { color: '#A9D342', gradientId: `gradient-${key}` };
                      const axisId = useDualAxis
                        ? (config.yAxisId ?? 'default')
                        : 'default';
                      return (
                        <Area
                          key={key}
                          type="linear"
                          dataKey={key}
                          yAxisId={axisId}
                          stroke={config.color}
                          fill={`url(#${config.gradientId})`}
                          strokeWidth={2}
                          dot={false}
                          isAnimationActive={!isMobile}
                          activeDot={
                            isMobile
                              ? false
                              : {
                                  r: 4,
                                  fill: config.color,
                                  stroke: config.color,
                                  strokeWidth: 2,
                                }
                          }
                        />
                      );
                    })
                  )}
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-full items-center justify-center">
                <p className="text-zinc-400 text-sm">{t("noData")}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {!isLegacy && activeStatusKeysOrdered.length > 0 && (
        <div className="flex justify-center flex-wrap gap-3 px-4 border-t border-[#27272A] p-6">
          {activeStatusKeysOrdered.map((key) => {
            const config = seriesConfig[key] ?? { color: '#A9D342', gradientId: `gradient-${key}` };
            const labelKey = seriesTranslationNs === 'signatures'
              ? `${seriesTranslationNs}:chart.${key}`
              : `${seriesTranslationNs}:${key}`;
            return (
              <div key={key} className="flex items-center gap-1.5">
                <div
                  className="w-2 h-2 rounded-full shrink-0"
                  style={{ backgroundColor: config.color }}
                />
                <span className="text-xs text-zinc-400">{t(labelKey)}</span>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
